import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Button, Tooltip,
} from '@mui/material';
import {
  DeleteSweep, DragIndicator, LibraryAdd, RemoveCircleOutline, Shuffle,
} from '@mui/icons-material';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { TrackArt, TrackPlayButton, TRACK_ACTION_ICON_SX } from './sonosTrackUi';
import SoundBars from './SoundBars';
import RepeatModeButton from './RepeatModeButton';
import SonosConfirmDialog from './SonosConfirmDialog';
import AddToPlaylistDialog from './AddToPlaylistDialog';
import { RepeatMode } from './sonosPlayMode';
import { SONOS_BLUE, SURFACE_BORDER, sonosIconTapSx, sonosIconTapDangerSx, sonosTextButtonTapSx } from './sonosUiStyles';
import type { LocalQueueItem } from './localQueueStore';

const INDICATOR_SX = {
  width: 36,
  height: 36,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ml: 0.5,
} as const;

interface LocalQueuePanelProps {
  items: LocalQueueItem[];
  currentIndex: number;
  playing: boolean;
  canControl?: boolean;
  shuffle: boolean;
  repeat: RepeatMode;
  onShuffleChange: (v: boolean) => void;
  onRepeatChange: (mode: RepeatMode) => void;
  onReorder: (from: number, to: number) => void;
  onRemove: (index: number) => void;
  onClear: () => void;
  onPlayAt: (index: number) => void;
  onError?: (msg: string) => void;
  onAddedToPlaylist?: () => void;
}

const SortableRow: React.FC<{
  id: string;
  item: LocalQueueItem;
  index: number;
  isCurrent: boolean;
  playing: boolean;
  canControl: boolean;
  onRemove: () => void;
  onPlay: () => void;
  onAddToPlaylist: () => void;
}> = ({
  id, item, index, isCurrent, playing, canControl, onRemove, onPlay, onAddToPlaylist,
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const showBars = isCurrent && playing;

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.75,
        borderBottom: `1px solid ${SURFACE_BORDER}`,
        touchAction: 'pan-y',
      }}
    >
      <IconButton
        size="small"
        disabled={!canControl}
        onClick={onRemove}
        disableRipple
        sx={{ color: '#e53935', p: 0.25, flexShrink: 0, ...sonosIconTapDangerSx }}
      >
        <RemoveCircleOutline sx={{ fontSize: 22 }} />
      </IconButton>
      <TrackArt url={item.image_url} size={40} />
      <Box sx={{ flex: 1, minWidth: 0, pr: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}>
          <Typography sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
          }}>
            {item.title || `Track ${index + 1}`}
          </Typography>
          <Box onPointerDown={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
            <Tooltip title={t('musicSystem.search.addToPlaylist')}>
              <span>
                <IconButton
                  size="small"
                  onClick={onAddToPlaylist}
                  disabled={!canControl || !item.uri.startsWith('spotify:track:')}
                  sx={{ color: '#aaa', ...TRACK_ACTION_ICON_SX }}
                >
                  <LibraryAdd sx={{ fontSize: 22 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={INDICATOR_SX} onPointerDown={(e) => e.stopPropagation()}>
            {showBars ? (
              <SoundBars active height={16} sx={{ mr: 0, width: 22 }} />
            ) : (
              <TrackPlayButton disabled={!canControl} onClick={onPlay} />
            )}
          </Box>
        </Box>
        <Typography sx={{
          color: '#999',
          fontSize: 11,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {item.artist || ''}
        </Typography>
      </Box>
      <IconButton
        size="small"
        disabled={!canControl}
        disableRipple
        sx={{ color: '#888', p: 0.25, flexShrink: 0, cursor: 'grab', touchAction: 'none', ...sonosIconTapSx }}
        {...attributes}
        {...listeners}
      >
        <DragIndicator sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

const LocalQueuePanel: React.FC<LocalQueuePanelProps> = ({
  items,
  currentIndex,
  playing,
  canControl = true,
  shuffle,
  repeat,
  onShuffleChange,
  onRepeatChange,
  onReorder,
  onRemove,
  onClear,
  onPlayAt,
  onError,
  onAddedToPlaylist,
}) => {
  const { t } = useTranslation();
  const [removePrompt, setRemovePrompt] = useState<{ index: number; title: string } | null>(null);
  const [addToPlaylistOpen, setAddToPlaylistOpen] = useState(false);
  const [addToPlaylistUri, setAddToPlaylistUri] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 8 } }),
  );

  const ids = items.map((it) => it.id);

  const handleDragEnd = (event: DragEndEvent) => {
    if (!canControl) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(oldIndex, newIndex);
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1,
        px: 0.5,
        minHeight: 40,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.25 }}>
          <IconButton
            onClick={() => onShuffleChange(!shuffle)}
            disabled={!canControl}
            disableRipple
            aria-label={t('musicSystem.queue.shuffle')}
            sx={{
              color: shuffle ? SONOS_BLUE : '#aaa',
              p: 0.75,
              ...(shuffle ? {
                bgcolor: 'rgba(35,145,255,0.12)',
                '&:active': { bgcolor: 'rgba(35,145,255,0.28)', transform: 'scale(0.93)' },
              } : sonosIconTapSx),
            }}
          >
            <Shuffle />
          </IconButton>
          <RepeatModeButton
            mode={repeat}
            disabled={!canControl}
            onChange={onRepeatChange}
          />
        </Box>
        <Button
          size="small"
          startIcon={<DeleteSweep sx={{ fontSize: 18 }} />}
          disabled={!canControl || items.length === 0}
          onClick={onClear}
          disableRipple
          sx={{
            color: '#e57373',
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 600,
            mr: 0.25,
            ...sonosTextButtonTapSx,
            '&:active': { bgcolor: 'rgba(229,57,53,0.14)', color: '#ff8a80' },
          }}
        >
          {t('musicSystem.queue.clear')}
        </Button>
      </Box>
      {items.length === 0 ? (
        <Typography sx={{ color: '#999', fontSize: 12, textAlign: 'center', py: 2 }}>
          {t('musicSystem.queue.empty')}
        </Typography>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {items.map((item, i) => (
              <SortableRow
                key={item.id}
                id={item.id}
                item={item}
                index={i}
                isCurrent={i === currentIndex}
                playing={playing}
                canControl={canControl}
                onRemove={() => setRemovePrompt({
                  index: i,
                  title: item.title?.trim() || `Track ${i + 1}`,
                })}
                onPlay={() => onPlayAt(i)}
                onAddToPlaylist={() => {
                  setAddToPlaylistUri(item.uri);
                  setAddToPlaylistOpen(true);
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      <SonosConfirmDialog
        open={Boolean(removePrompt)}
        historyKey="local-queue-remove-track"
        title={t('musicSystem.queue.removeConfirmTitle')}
        message={t('musicSystem.queue.removeConfirmBody', { name: removePrompt?.title ?? '' })}
        onCancel={() => setRemovePrompt(null)}
        onConfirm={() => {
          if (removePrompt) onRemove(removePrompt.index);
          setRemovePrompt(null);
        }}
      />
      <AddToPlaylistDialog
        open={addToPlaylistOpen}
        trackUri={addToPlaylistUri}
        onClose={() => { setAddToPlaylistOpen(false); setAddToPlaylistUri(null); }}
        onAdded={onAddedToPlaylist}
        onError={onError}
      />
    </Box>
  );
};

export default LocalQueuePanel;
